/* reference: */
/* https://dev.to/ynab/a-semantic-color-system-the-theory-hk7 */
/* https://www.figma.com/community/file/817833862700426706 */

/* #NOTES */
/* every possible color value */
/* When the color usage is not semantically clear, use the palette colors first. */
export const PALETTE_COLORS = {
  NEUTRAL_100: '#FFFFFF',
  NEUTRAL_200: '#FFFDF5',
  NEUTRAL_300: '#FFF9ED',
  NEUTRAL_400: '#FBF7E8',
  NEUTRAL_800: '#9C9891',
  NEUTRAL_900: '#383735',

  PRIMARY_100: '#F1F3DD',
  PRIMARY_200: '#DCEAC5',
  PRIMARY_300: '#D5E7BF',
  PRIMARY_500: '#74BD52',
  PRIMARY_600: '#A7BD9C',
  PRIMARY_800: '#5D865A',
  PRIMARY_900: '#456242',

  ACCENT_100: '#FEF4DC',
  ACCENT_300: '#FFE199',
  ACCENT_500: '#F1B931',
  ACCENT_700: '#FF9900',

  INFORMATION_100: '#F2F4F4',
  INFORMATION_300: '#E9F8EB',
  INFORMATION_800: '#1A4B94',

  DESTRUCTIVE_100: '#FFC9C1',
  DESTRUCTIVE_500: '#FF4B30',
  DESTRUCTIVE_900: '#9C5348',
} as const;

/* #NOTES */
/* Semantic colors are colors based on how they are semantically used, */
/* commonly used along with components. */
/* Each and every semantic color points to a color from the base palette. */
export const SEMANTIC_COLORS = {
  /* button */
  PRIMARY_BUTTON_BACKGROUND: PALETTE_COLORS.NEUTRAL_900,
  PRIMARY_BUTTON_BACKGROUND_DISABLED: PALETTE_COLORS.NEUTRAL_800,
  PRIMARY_BUTTON_TEXT: PALETTE_COLORS.NEUTRAL_300,

  SECONDARY_BUTTON_BACKGROUND: PALETTE_COLORS.NEUTRAL_300,
  SECONDARY_BUTTON_TEXT: PALETTE_COLORS.NEUTRAL_900,
  SECONDARY_BUTTON_BORDER: PALETTE_COLORS.NEUTRAL_900,

  DEFAULT_COUNTER_BUTTON_BACKGROUND: PALETTE_COLORS.PRIMARY_500,
  DEFAULT_COUNTER_BUTTON_TEXT: PALETTE_COLORS.NEUTRAL_300,

  /* link */
  PRIMARY_LINK: PALETTE_COLORS.PRIMARY_500,
  SECONDARY_LINK: PALETTE_COLORS.NEUTRAL_900,

  /* title */
  DEFAULT_TITLE: PALETTE_COLORS.PRIMARY_800,
  SECONDARY_TITLE: PALETTE_COLORS.NEUTRAL_900,
  ERROR_TITLE: PALETTE_COLORS.DESTRUCTIVE_900,
  DEFAULT_TITLE_BACKGROUND: PALETTE_COLORS.PRIMARY_800,
  DEFAULT_TITLE_BACKGROUND_TEXT: PALETTE_COLORS.NEUTRAL_300,
  TAB_TITLE_BACKGROUND: PALETTE_COLORS.PRIMARY_500,
  TAB_TITLE_BACKGROUND_TEXT: PALETTE_COLORS.NEUTRAL_300,

  /* text */
  DEFAULT_TEXT: PALETTE_COLORS.NEUTRAL_900,
  PLACEHOLDER_TEXT: PALETTE_COLORS.NEUTRAL_800,
  COMMENT_TEXT: PALETTE_COLORS.NEUTRAL_800,

  SUCCESS_TEXT: PALETTE_COLORS.PRIMARY_500,
  WARNING_TEXT: PALETTE_COLORS.ACCENT_700,
  ERROR_TEXT: PALETTE_COLORS.DESTRUCTIVE_500,
  HIGHLIGHT_TEXT: PALETTE_COLORS.DESTRUCTIVE_500,

  PRIMARY_TEXT: PALETTE_COLORS.PRIMARY_500,
  SECONDARY_TEXT: PALETTE_COLORS.PRIMARY_800,

  /* tabs */
  DEFAULT_TAB_BACKGROUND: PALETTE_COLORS.PRIMARY_200,
  ACTIVE_TAB_BACKGROUND: PALETTE_COLORS.PRIMARY_500,
  DEFAULT_TAB_TITLE: PALETTE_COLORS.NEUTRAL_300,

  /* border | divider */
  PRIMARY_BORDER: PALETTE_COLORS.PRIMARY_500,
  SECONDARY_BORDER: PALETTE_COLORS.PRIMARY_800,
  ERROR_BORDER: PALETTE_COLORS.DESTRUCTIVE_900,

  /* field box */
  DEFAULT_FIELD_BOX_BACKGROUND: PALETTE_COLORS.PRIMARY_100,
  ERROR_FIELD_BOX_BACKGROUND: PALETTE_COLORS.DESTRUCTIVE_100,

  /* input | textarea */
  DEFAULT_INPUT_BACKGROUND: PALETTE_COLORS.NEUTRAL_300,
  DEFAULT_TEXTAREA_BACKGROUND: PALETTE_COLORS.NEUTRAL_300,

  /* message */
  DEFAULT_MESSAGE_BACKGROUND: PALETTE_COLORS.NEUTRAL_200,

  /* progress bar */
  DEFAULT_PROGRESS_BACKGROUND: PALETTE_COLORS.PRIMARY_200,
  DEFAULT_PROGRESS_BAR: PALETTE_COLORS.PRIMARY_500,

  /* collapse */
  DEFAULT_COLLAPSE_PANEL_BACKGROUND: PALETTE_COLORS.NEUTRAL_400,

  /* description */
  DEFAULT_DESCRIPTION_BACKGROUND: PALETTE_COLORS.PRIMARY_300,
  ERROR_DESCRIPTION_BACKGROUND: PALETTE_COLORS.DESTRUCTIVE_100,

  /* chips */
  DEFAULT_CHIPS_BACKGROUND: PALETTE_COLORS.PRIMARY_100,

  /* card */
  PRIMARY_CARD_BACKGROUND: PALETTE_COLORS.PRIMARY_100,
  SECONDARY_CARD_BACKGROUND: PALETTE_COLORS.PRIMARY_300,
  DEFAULT_CARD_BACKGROUND: PALETTE_COLORS.PRIMARY_200,
  BASIC_CARE_TAKER_TIER_LEVEL_CARD_BACKGROUND: PALETTE_COLORS.ACCENT_100,
  PLATINUM_CARE_TAKER_TIER_LEVEL_CARD_BACKGROUND: PALETTE_COLORS.PRIMARY_100,
  DIAMOND_CARE_TAKER_TIER_LEVEL_CARD_BACKGROUND: PALETTE_COLORS.INFORMATION_100,
  PROFESSIONAL_CARE_TAKER_TIER_LEVEL_CARD_BACKGROUND: PALETTE_COLORS.INFORMATION_300,

  /* icon */
  DEFAULT_ICON: PALETTE_COLORS.NEUTRAL_300,
  PRIMARY_ICON: PALETTE_COLORS.PRIMARY_500,
  SECONDARY_ICON: PALETTE_COLORS.PRIMARY_800,
  ACCENT_ICON: PALETTE_COLORS.ACCENT_500,
  WARNING_ICON: PALETTE_COLORS.ACCENT_700,
  ERROR_ICON: PALETTE_COLORS.DESTRUCTIVE_900,

  /* checkbox */
  DEFAULT_CHECKBOX_BACKGROUND: PALETTE_COLORS.NEUTRAL_100,
  DEFAULT_CHECKBOX_BORDER: PALETTE_COLORS.PRIMARY_800,

  /* popup | modal */
  DEFAULT_POPUP_BACKGROUND: PALETTE_COLORS.NEUTRAL_300,
  DEFAULT_MODAL_BACKGROUND: PALETTE_COLORS.NEUTRAL_300,

  /* page */
  DEFAULT_PAGE_BACKGROUND: PALETTE_COLORS.NEUTRAL_300,

  /* loading spinner */
  DEFAULT_LOADING_SPINNER: PALETTE_COLORS.PRIMARY_800,
} as const;
